/**
 * Cookie Policy Page
 * Comprehensive documentation of cookies and tracking
 * Multi-jurisdictional compliance (GDPR, CCPA, PDPA)
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Cookie, Shield, Settings } from 'lucide-react';
import { COOKIE_LIST, TRACKING_SERVICES } from '../types/consent';
import { useState } from 'react';
import CookiePreferencesModal from '../components/modals/CookiePreferencesModal';

export default function CookiePolicy() {
  const [showPreferences, setShowPreferences] = useState(false);

  return (
    <>
      <Helmet>
        <title>Cookie Policy - EmploSkills</title>
        <meta
          name="description"
          content="Learn about how EmploSkills uses cookies and similar tracking technologies. Comprehensive cookie policy for GDPR, CCPA, and PDPA compliance."
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Cookie className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Cookie Policy</h1>
                <p className="text-gray-600 mt-1">Last updated: January 2025</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <p className="text-sm text-blue-900">
                <strong>Quick Actions:</strong> You can manage your cookie preferences at any time.
              </p>
              <button
                onClick={() => setShowPreferences(true)}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
              >
                <Settings className="w-4 h-4" />
                Manage Cookie Preferences
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
              <div className="prose prose-sm max-w-none text-gray-700">
                <p>
                  Cookies are small text files that are placed on your device (computer, smartphone, or tablet)
                  when you visit a website. They are widely used to make websites work more efficiently
                  and provide information to website owners.
                </p>
                <p className="mt-3">
                  We use cookies and similar tracking technologies (such as web beacons, pixels, and local storage)
                  to enhance your browsing experience, analyze site traffic, and understand visitor behavior.
                </p>
              </div>
            </section>

            {/* Why We Use Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why We Use Cookies</h2>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Essential Functionality</h3>
                    <p className="text-sm text-gray-600">
                      Ensure the website functions properly, remember your preferences, and maintain security
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Performance & Analytics</h3>
                    <p className="text-sm text-gray-600">
                      Understand how visitors use our website to improve user experience and identify issues
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Marketing & Advertising</h3>
                    <p className="text-sm text-gray-600">
                      Deliver relevant advertisements and measure the effectiveness of our marketing campaigns
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Types of Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of Cookies We Use</h2>

              {/* Essential Cookies */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">
                    ESSENTIAL
                  </span>
                  Essential Cookies (Always Active)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  These cookies are necessary for the website to function and cannot be disabled.
                  Without these cookies, services you have requested cannot be provided.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Cookie Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Purpose</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Expiration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {COOKIE_LIST.filter(c => c.category === 'essential').map((cookie, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{cookie.name}</td>
                          <td className="px-4 py-3 text-gray-700">{cookie.purpose}</td>
                          <td className="px-4 py-3 text-gray-600">{cookie.expiration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                    ANALYTICS
                  </span>
                  Analytics Cookies (Optional)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  These cookies help us understand how visitors interact with our website by collecting
                  and reporting information anonymously. All data is aggregated and anonymized.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Cookie Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Purpose</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Expiration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {COOKIE_LIST.filter(c => c.category === 'analytics').map((cookie, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{cookie.name}</td>
                          <td className="px-4 py-3 text-gray-700">{cookie.purpose}</td>
                          <td className="px-4 py-3 text-gray-600">{cookie.expiration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded">
                    MARKETING
                  </span>
                  Marketing Cookies (Optional)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  These cookies track your activity across websites to deliver advertisements that are
                  relevant to you. They also measure the effectiveness of advertising campaigns.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Cookie Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Purpose</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-900">Expiration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {COOKIE_LIST.filter(c => c.category === 'marketing').map((cookie, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{cookie.name}</td>
                          <td className="px-4 py-3 text-gray-700">{cookie.purpose}</td>
                          <td className="px-4 py-3 text-gray-600">{cookie.expiration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
              <p className="text-sm text-gray-600 mb-4">
                We work with the following third-party service providers who may set cookies on your device:
              </p>
              <div className="space-y-4">
                {TRACKING_SERVICES.map((service, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">{service.name}</h3>
                        <p className="text-xs text-gray-500">{service.provider}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-bold rounded ${
                        service.category === 'analytics'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {service.category.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{service.description}</p>
                    <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                      <div>
                        <span className="font-medium text-gray-900">Purpose:</span>
                        <p className="text-gray-600">{service.purpose}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Data Retention:</span>
                        <p className="text-gray-600">{service.retention}</p>
                      </div>
                    </div>
                    {service.thirdPartySharing && (
                      <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-2 text-xs text-orange-900">
                        This service shares data with third parties
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* International Data Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
              <p className="text-sm text-gray-600">
                Some of our third-party service providers (Google, Microsoft, Meta) are based in the
                United States. When you consent to analytics or marketing cookies, your data may be
                transferred to and processed in the United States or other countries outside your
                jurisdiction. These companies comply with applicable data protection frameworks and
                implement appropriate safeguards for international data transfers.
              </p>
            </section>

            {/* Managing Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Manage Cookies</h2>
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">On Our Website</h3>
                  <p>
                    You can manage your cookie preferences at any time using our Cookie Preferences Center:
                  </p>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                  >
                    <Settings className="w-4 h-4" />
                    Open Cookie Preferences
                  </button>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">In Your Browser</h3>
                  <p className="mb-2">
                    Most web browsers allow you to control cookies through settings. To find out more
                    about cookies, including how to manage and delete them, visit:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Chrome</a></li>
                    <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mozilla Firefox</a></li>
                    <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Apple Safari</a></li>
                    <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft Edge</a></li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-900">
                    <strong>Please note:</strong> If you disable all cookies, some features of our website
                    may not function properly, and your user experience may be limited.
                  </p>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Privacy Rights</h2>
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <h3 className="font-semibold text-gray-900">GDPR Rights (EU Residents)</h3>
                  <p>Access, rectification, erasure, restriction, portability, and objection to processing</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">CCPA Rights (California Residents)</h3>
                  <p>Know, delete, opt-out of sale/sharing, and non-discrimination</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">PDPA Rights (Singapore/Asia Residents)</h3>
                  <p>Access, correction, and withdrawal of consent</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                For more information about your privacy rights, please see our{' '}
                <Link to="/privacy-policy" className="text-primary hover:underline font-medium">
                  Privacy Policy
                </Link>.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Questions About Cookies?</h2>
              <p className="text-sm text-gray-700 mb-3">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="text-sm text-gray-700">
                <p><strong>Email:</strong>{' '}
                  <a href="mailto:contact@emploskills.com" className="text-primary hover:underline">
                    contact@emploskills.com
                  </a>
                </p>
                <p className="mt-2">
                  <strong>Related Policies:</strong>{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <CookiePreferencesModal
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
        />
      )}
    </>
  );
}
